// Stage 1 batch-classification mutation helpers.
//
// Kept outside Stage1DataEntry.jsx so the persistence-facing document shape
// can be regression-tested without mounting the full intake page.

import { isEstimatorNotesDocument } from '../utils/document-classification';
import { detectRfqByContent } from '../utils/rfq-content-detector';
import { readStage1ScopeOfWorks } from './stage1-premade-scope';
import { aiDocumentFormat } from '../utils/ai-document-label';

const AI_BATCH_CLASSIFICATION_SOURCE = 'ai-batch-classifier-content';
const DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE = 'document-type-classifier-content';
const DETERMINISTIC_RFQ_PRESERVATION_SOURCE = 'stage1-preclassified-rfq-preservation';
const RFQ_FALLBACK_SOURCE = 'stage1-batch-rfq-fallback';
const HIGH_CONFIDENCE_DETERMINISTIC_VETO_THRESHOLD = 90;

export interface Stage1DeterministicClassificationMetadataOptions {
    class?: string;
    confidence?: number;
    evidence?: any;
}

export interface Stage1BatchClassificationOptions {
    candidateClassification?: string;
    candidateConfidence?: number;
    candidateReason?: string;
    preserveRfq?: boolean;
}

export interface Stage1BatchClassificationResult {
    // undefined when the AI batch row carried no classification: the caller
    // passes the parsed AI row through unvalidated.
    finalClass: string | undefined;
    confidence: number;
    reason: string;
    deterministicVeto?: boolean;
}

function persistedClassificationSource(document: any): string {
    const explicit = [document?.classificationSource, document?.classSource]
        .find(value => typeof value === 'string' && value.trim());
    if (explicit && explicit !== AI_BATCH_CLASSIFICATION_SOURCE) return explicit.trim();
    return DETERMINISTIC_RFQ_PRESERVATION_SOURCE;
}

function stampClassificationSource(document: any, source: string): void {
    document.classSource = source;
    document.classificationSource = source;
}

export function buildStage1DeterministicClassificationMetadata(classification: Stage1DeterministicClassificationMetadataOptions = {}): any {
    return {
        // Persist the deterministic result as the canonical class as well as
        // the initial `type`. Downstream authority checks only treat this
        // field as canonical because the matching content-aware source is
        // stamped beside it.
        ...(classification?.class ? { class: classification.class } : {}),
        classifiedConfidence: classification?.confidence,
        classifiedEvidence: classification?.evidence,
        classSource: DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE,
        classificationSource: DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE,
    };
}

function shouldPreserveDeterministicReport(document: any, candidateClassification: any): boolean {
    const source = String(document?.classificationSource || document?.classSource || '')
        .trim()
        .toLowerCase();
    const deterministicClass = String(document?.class || document?.type || '')
        .trim()
        .toLowerCase();
    const confidence = Number(document?.classifiedConfidence);

    // A high-confidence, content-derived report must not become technical
    // plan authority merely because the non-deterministic batch pass calls it
    // a plan. This is particularly important for DBP Design Compliance
    // Declarations, which name their related plans without being the plans.
    // The narrow source/confidence/class/destination gate deliberately leaves
    // low-confidence results and later operator corrections reclassifiable.
    return source === DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE
        && deterministicClass === 'report'
        && candidateClassification === 'plan'
        && Number.isFinite(confidence)
        && confidence >= HIGH_CONFIDENCE_DETERMINISTIC_VETO_THRESHOLD;
}

// The business's own scope notes (D6). The AI batch prompt has no notes
// class, so it labels them "competitor" or even "rfq". The deterministic
// class rests on the filename and the absence of any other business, which
// the AI cannot see better, so it is kept.
function isDeterministicEstimatorNotes(document: any): boolean {
    const source = String(document?.classificationSource || document?.classSource || '')
        .trim()
        .toLowerCase();
    const deterministicClass = String(document?.class || document?.type || '')
        .trim()
        .toLowerCase();
    return source === DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE && deterministicClass === 'estimator_notes';
}

export function applyStage1BatchClassification(document: any, {
    candidateClassification,
    candidateConfidence,
    candidateReason,
    preserveRfq = false,
}: Stage1BatchClassificationOptions = {}): Stage1BatchClassificationResult | null {
    if (!document || typeof document !== 'object') return null;

    if (!preserveRfq && shouldPreserveDeterministicReport(document, candidateClassification)) {
        const finalClass = 'report';
        const deterministicConfidence = Number(document.classifiedConfidence);
        document.class = finalClass;
        document.type = finalClass;
        document.aiClassification = finalClass;
        document.aiClassificationConfidence = deterministicConfidence;
        document.aiClassificationReason = `Preserved high-confidence deterministic report; AI batch suggested "${candidateClassification}" (${candidateConfidence || 0}%)`;
        document.aiBatchSuggestedClassification = candidateClassification;
        document.aiBatchSuggestedConfidence = candidateConfidence;
        document.aiBatchSuggestedReason = candidateReason;
        stampClassificationSource(document, DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE);

        return {
            finalClass,
            confidence: deterministicConfidence,
            reason: document.aiClassificationReason,
            deterministicVeto: true,
        };
    }

    if (!preserveRfq && isDeterministicEstimatorNotes(document)) {
        const finalClass = 'estimator_notes';
        const deterministicConfidence = Number(document.classifiedConfidence);
        document.class = finalClass;
        document.type = finalClass;
        document.aiClassification = finalClass;
        document.aiClassificationConfidence = deterministicConfidence;
        document.aiClassificationReason = `Kept the business's own estimator notes; AI batch suggested "${candidateClassification}" (${candidateConfidence || 0}%)`;
        document.aiBatchSuggestedClassification = candidateClassification;
        document.aiBatchSuggestedConfidence = candidateConfidence;
        document.aiBatchSuggestedReason = candidateReason;
        stampClassificationSource(document, DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE);

        return {
            finalClass,
            confidence: deterministicConfidence,
            reason: document.aiClassificationReason,
            deterministicVeto: true,
        };
    }

    // CN4 (Peter 2026-10-13: "file names shouldnt dictate anything. only the
    // contents."): the batch pass sees the file name, and on the office copy it
    // called the business's own quote "scope_of_works". A document is the
    // scope of works only when its text reads as one; otherwise the class the
    // content classifier gave it at import is kept.
    if (!preserveRfq && candidateClassification === 'scope_of_works' && !readStage1ScopeOfWorks(document).isScopeOfWorks) {
        const importClass = String(document.class || document.type || '').trim();
        const finalClass = importClass && importClass !== 'scope_of_works' ? importClass : 'other';
        const deterministicConfidence = Number(document.classifiedConfidence);
        document.class = finalClass;
        document.type = finalClass;
        document.aiClassification = finalClass;
        document.aiClassificationConfidence = deterministicConfidence;
        document.aiClassificationReason = `Kept the class read from the text; AI batch suggested "${candidateClassification}" (${candidateConfidence || 0}%), but the text does not read as a scope of works`;
        document.aiBatchSuggestedClassification = candidateClassification;
        document.aiBatchSuggestedConfidence = candidateConfidence;
        document.aiBatchSuggestedReason = candidateReason;
        stampClassificationSource(document, DETERMINISTIC_CONTENT_CLASSIFICATION_SOURCE);

        return {
            finalClass,
            confidence: deterministicConfidence,
            reason: document.aiClassificationReason,
            deterministicVeto: true,
        };
    }

    const finalClass = preserveRfq ? 'rfq' : candidateClassification;
    document.type = finalClass;
    document.aiClassification = finalClass;
    document.aiClassificationConfidence = preserveRfq
        ? Math.max(80, candidateConfidence || 0)
        : candidateConfidence;
    document.aiClassificationReason = preserveRfq
        ? `Kept as the RFQ; AI batch suggested "${candidateClassification}" (${candidateConfidence}%)`
        : candidateReason;
    stampClassificationSource(
        document,
        preserveRfq ? persistedClassificationSource(document) : AI_BATCH_CLASSIFICATION_SOURCE,
    );

    return {
        finalClass,
        confidence: document.aiClassificationConfidence,
        reason: document.aiClassificationReason,
    };
}

/**
 * The document the batch fallback promotes to RFQ when the AI pass left none:
 * the first whose text reads as the client's request (CN2, Peter 2026-10-13:
 * "file names shouldnt dictate anything. only the contents."). Neither a file
 * name nor being an email makes a document the candidate. The business's own
 * estimator notes are never a candidate (D6).
 */
export function selectStage1RfqFallbackCandidate(docs: any[]): any {
    const eligible = (Array.isArray(docs) ? docs : [])
        .filter(doc => doc && typeof doc === 'object'
            && !isEstimatorNotesDocument(doc)
            && !isDeterministicEstimatorNotes(doc));
    return eligible.find(doc => detectRfqByContent(doc.content ?? doc.text ?? '').isRfq) || null;
}

export function applyStage1BatchRfqFallback(document: any): any {
    if (!document || typeof document !== 'object') return null;
    if (isEstimatorNotesDocument(document) || isDeterministicEstimatorNotes(document)) return null;

    document.type = 'rfq';
    document.aiClassification = 'rfq';
    document.aiClassificationConfidence = 60;
    document.aiClassificationReason = 'Fallback: no doc classified as RFQ; promoted the document whose text reads as the request';
    stampClassificationSource(document, RFQ_FALLBACK_SOURCE);

    return document;
}

/**
 * The document list the AI batch classifier reads (BATCH_CLASSIFY_PROMPT
 * `${documentList}`). CN5 (Peter, 2026-10-13: "file names shouldnt dictate
 * anything. only the contents."): each document appears as its position and
 * format, never its file name, so a name such as "Scope.pdf" or "RFQ.pdf"
 * cannot sway the label. The answer is read back by that position
 * (`index`). Email headers and the document's text are content and stay.
 */
export function buildStage1BatchClassifyDocumentList(docs: any[], summaryLength = 500): string {
    return (Array.isArray(docs) ? docs : []).map((doc, i) => {
        const format = aiDocumentFormat(doc);
        let summary = format ? `Document ${i} (${format})` : `Document ${i}`;
        if (doc?.isEmail) summary += `\n  Email from: ${doc.emailFrom || 'unknown'}, Subject: ${doc.emailSubject || 'none'}`;
        if (doc?.content && doc.content.length > 30 && !doc.content.startsWith('[PDF ') && !doc.content.startsWith('[Could not')) {
            summary += `\n  Content: ${doc.content.substring(0, summaryLength)}`;
        } else {
            summary += `\n  Content: (no readable text extracted)`;
        }
        return summary;
    }).join('\n\n');
}
